/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Tabs,
  Card,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';

import { checkToken } from 'api/utils';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import {
  autorizarPeriodoPagoISSSTE,
  deletePeriodoPagoISSSTE,
  getConceptosPeriodosPagoSS,
  getPeriodoPagoISSSTE,
  getPeriodosPagoISSSTE,
  patchPeriodoPagoISSSTE,
  postPeriodoPagoISSSTE,
} from 'api/catalogos/PeriodoPagosIssste';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import CalendarioPagosISSSTEDetalle from './CalendarioPagosISSSTEDetalle';

// Nivel de Seguridad Social correspondiente a ISSSTE.
const NIVEL_ISSSTE = 4;

export const CalendarioPagosISSSTEPermission = {
  permissionModel: 'periododeseguroissste',
  catalogsModels: [],
  tabsModels: [],
};

const { TabPane } = Tabs;
const CalendarioPagosISSSTE = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [conceptosPeriodos, setConceptosPeriodoss] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPeriodosPagoISSSTE();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _periodosFiscales,
        _conceptosPeriodos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getConceptosPeriodosPagoSS(),
        getPeriodosPagoISSSTE(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setPeriodosFiscales(_periodosFiscales);
        setConceptosPeriodoss((_conceptosPeriodos || [])
          .filter((item) => item.seguridad_social === NIVEL_ISSSTE));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _periodo = await getPeriodoPagoISSSTE(match.id);
    setTimeout(() => {
      if (_periodo) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_periodo));
          setDisableFields(_periodo?.estados_globales !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });

    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchPeriodoPagoISSSTE(key, values) : postPeriodoPagoISSSTE(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePeriodoPagoISSSTE(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar el periodo seleccionado');
        return;
      }
      const saved = await autorizarPeriodoPagoISSSTE(key);
      if (saved) {
        const _data = await getPeriodosPagoISSSTE();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Ejercicio',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 400,
      render: (val) => (val && periodosFiscales ? periodosFiscales.find((item) => item.id === val)?.periodo : 'N/A'),
    },
    {
      titleText: 'Concepto-Periodicidad',
      dataIndex: 'concepto_periodicidad',
      key: 'concepto_periodicidad',
      width: 400,
      render: (val) => (val && conceptosPeriodos ? conceptosPeriodos.find((item) => item.id === val)?.periodicidad : 'N/A'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            customActions={[
              {
                onClick: () => setVisibleAutorizar(true),
                permission: { model: 'periododeseguroissste', action: 'autorizar' },
                icon: <CheckCircleOutlined />,
                text: 'Autorizar',
                disabledBySelection: true,
                disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
                className: 'success',
              },
            ]}
            disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Periodos De Pago Del ISSSTE`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
              />
                      )}
            bordered={false}
          >
            <Tabs
              onChange={setCurrentTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <TabPane
                tab="Periodos De Pago Del ISSSTE"
                key="general"
                forceRender
              >

                <Form
                  layout="vertical"
                  form={form}
                  onFinish={onFinish}
                  scrollToFirstError
                  name="general"
                  initialValues={{
                    estados_globales: ESTATUS_ACTIVO,
                  }}
                >
                  <Row gutter={[16, 8]}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="periodo_fiscal"
                        label="Ejercicio"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          labelProp="periodo"
                          keyProp="id"
                          dataSource={periodosFiscales}
                          disabled={selectedRowKeys.length > 0}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="concepto_periodicidad"
                        label="Concepto - Periodicidad"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          labelProp="periodicidad"
                          keyProp="id"
                          dataSource={conceptosPeriodos}
                          disabled={selectedRowKeys.length > 0}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estatus"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          labelProp="descripcion"
                          keyProp="id"
                          dataSource={estados}
                          activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                          disabled
                          onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <CalendarioPagosISSSTEDetalle
                    parentId={selectedRowKeys[0]}
                    permission={permission}
                  />
                </Form>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`el periodo de pago del ISSSTE correspondiente al ejercicio ${
            periodosFiscales.find((item) => item.id === selectedData?.periodo_fiscal)?.periodo || 'N/A'
          } ${
            conceptosPeriodos.find((item) => item.id === selectedData?.concepto_periodicidad)?.periodicidad || 'N/A'
          }?`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`¿Desea autorizar el periodo de pago del ISSSTE correspondiente al ejercicio ${
            periodosFiscales.find((item) => item.id === selectedData?.periodo_fiscal)?.periodo || 'N/A'
          } y concepto ${
            conceptosPeriodos.find((item) => item.id === selectedData?.concepto_periodicidad)?.periodicidad || 'N/A'
          }?`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`¿Desea inactivar el periodo de pago del ISSSTE correspondiente al ejercicio ${
            periodosFiscales.find((item) => item.id === selectedData?.periodo_fiscal)?.periodo || 'N/A'
          } y concepto ${
            conceptosPeriodos.find((item) => item.id === selectedData?.concepto_periodicidad)?.periodicidad || 'N/A'
          }?`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default CalendarioPagosISSSTE;
